import {
	DmData,
	DmDataFactoryFromJS,
	DmDataPermissionsFactory,
	DmDataPermissionsFactoryFromJS,
	DmObjectType,
	IDmData,
	IDmDataPermissions,
	MainUtils,
	MessageBox,
	MessageBoxButtons,
	ServiceList,
	panelType
} from '@bimser/common';
import { findMessage } from '@bimser/components';
import { IBaseMutableAction } from '@bimser/core';
import { Map } from 'immutable';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { WebInterfaceBaseState } from '../../../../../common/base/state';
import openDrawerPanelComponent from '../../../../../common/components/panelComponent/sagas/openPanelComponent';
import { IRelatedPanel } from '../../../../../common/entities';
import { openNewPanelInsteadOfTheOtherOne } from '../../../../../common/sagas/commonSagas';
import { setSelectedItems, updatePanel } from '../../../actions';
import { DmPanel } from '../../../entities';
import { callGetObjectContext } from '../../../sagas';
import * as ViewerActions from '../actions/actionTypes';
import {
	BaseViewerPayload,
	OpenAdvancedViewerPayload,
	OpenBasicViewerPayload
} from '../entities/IOpenViewerPayload';
import { ViewerInitialProps } from '../entities/IViewerInitialProps';
import { ViewerPanelContentFactory } from '../entities/IViewerPanelContent';

export function* OpenViewerActionListener() {
	yield all([
		takeLatest(ViewerActions.OPEN_BASIC_VIEWER_ACTION, openBasicViewerListener),
		takeLatest(ViewerActions.OPEN_ADVANCED_VIEWER_ACTION, openAdvancedViewerListener)
	]);
}

function* openBasicViewerListener(action: IBaseMutableAction<OpenBasicViewerPayload>) {
	yield call(openBasicViewer, action.payload);
}

function* openAdvancedViewerListener(action: IBaseMutableAction<OpenAdvancedViewerPayload>) {
	yield call(openAdvancedViewer, action.payload);
}

/**
 * Sadece ilgili Dosya/Contenti görüntülemek için kullanılan fonksiyon.
 * Versiyon seçenekleri yoktur.
 */
export function* openBasicViewer(payload: OpenBasicViewerPayload) {
	try {
		if (!payload.itemId || !payload.secretKey || !payload.name) {
			return false;
		}
		const isDocumentExist = yield call(
			checkDocumentExist,
			payload.checkExistance,
			payload.type,
			payload.secretKey,
			payload.parentPanel
		);
		if (!isDocumentExist) {
			return false;
		}

		const closePanelId = yield call(getClosePanelId, payload);
		const permissions: IDmDataPermissions = payload.permissions
			? payload.permissions
			: yield call(getViewerPermissions, payload.secretKey);

		return yield call(
			openViewerPanel,
			{
				permissions,
				viewerPayload: {
					viewerType: 'basic',
					data: payload
				}
			},
			closePanelId,
			payload.name,
			payload.maskClosable
		);
	} catch (error) {
		console.error(error);
		return false;
	}
}

/**
 * Sadece dosya tipi için açılır. İlgili dosyanın versiyonlarını, contentlerini, ilişkilerini kontrol edebilir.
 */
export function* openAdvancedViewer(payload: OpenAdvancedViewerPayload) {
	try {
		if (!payload.dmData || payload.dmData.originType != DmObjectType.File) {
			return false;
		}
		const isDocumentExist = yield call(
			checkDocumentExist,
			payload.checkExistance,
			payload.dmData.type,
			payload.dmData.secretKey,
			payload.parentPanel
		);
		if (!isDocumentExist) {
			return false;
		}

		const closePanelId = yield call(getClosePanelId, payload);
		const permissions: IDmDataPermissions = yield call(getDmDataPermissions, payload.dmData);

		return yield call(
			openViewerPanel,
			{
				permissions,
				viewerPayload: {
					viewerType: 'advanced',
					data: payload
				}
			},
			closePanelId,
			payload.dmData.name,
			payload.maskClosable
		);
	} catch (error) {
		console.error(error);
		return false;
	}
}

function* openViewerPanel(
	viewerInitialProps: ViewerInitialProps,
	closePanelId: string,
	title: string,
	maskClosable?: boolean
) {
	const { noPanelActions, panelSize, parentPanel } = viewerInitialProps.viewerPayload.data;
	switch (viewerInitialProps.viewerPayload.data.openOn) {
		case 'modal':
		case 'drawer': {
			return yield call(openDrawerPanelComponent, {
				title,
				type: viewerInitialProps.viewerPayload.data.openOn,
				panelType: panelType.dmViewer,
				parentPanel: parentPanel,
				props: viewerInitialProps,
				addNewToStore: {
					enable: true,
					panelData: ViewerPanelContentFactory()
				},
				width: MainUtils.panelSizeToWidth(panelSize),
				maskClosable: maskClosable
			});
		}
		case 'panel':
		default: {
			viewerInitialProps.viewerPayload.data.openOn = 'panel';

			const panelGuid = yield call(openNewPanelInsteadOfTheOtherOne, closePanelId, {
				title,
				paneltype: panelType.dmViewer,
				parent: parentPanel,
				initialSize: panelSize || 3,
				size: panelSize || 3,
				HeaderActionsHide: noPanelActions,
				extraProps: viewerInitialProps,
				addNewToStore: {
					enable: true,
					panelData: ViewerPanelContentFactory()
				}
			});

			yield call(setActiveChildPanel, parentPanel, panelGuid);

			return panelGuid;
		}
	}
}

function* checkDocumentExist(
	checkExistance: boolean,
	type: DmObjectType,
	secretKey: string,
	parentPanel?: IRelatedPanel
) {
	try {
		//favoriler/son kullanılanlardan açılmıyorsa veya tipi kısayol değilse kontrol etmeye gerek yok.
		if (!(checkExistance || type === DmObjectType.Shortcut)) {
			return true;
		}

		const isDocumentExist: boolean =
			yield ServiceList.dm.DocumentManagement.Explorer.CheckDocumentExistAndAuthorize.callPromise(
				{ secretKey: secretKey },
				true
			);

		if (!isDocumentExist) {
			return yield call(afterCheckDocumentExist, parentPanel);
		}

		return true;
	} catch (error) {
		const errorCode: string = MainUtils.getTaleInnerException(
			(error.exception as any).data.exception
		);

		if (errorCode == 'DMS__EXPL_0077') {
			return yield call(afterCheckDocumentExist, parentPanel);
		}
	}
}

function* afterCheckDocumentExist(parentPanel?: IRelatedPanel) {
	const panelId = parentPanel?.panelType === panelType.dmExplorer ? parentPanel.panelGuid : null;
	yield MessageBox.warning({
		title: findMessage.get('101186'),
		content: findMessage.get('101197'),
		buttons: MessageBoxButtons.OK
	});

	yield put(
		setSelectedItems({
			panelId,
			selectedObjectIds: []
		})
	);
	return false;
}

function* getClosePanelId(payload: BaseViewerPayload) {
	let closePanelId: string = null;

	if (payload.closePanelGuid) {
		return payload.closePanelGuid;
	}

	switch (payload.parentPanel?.panelType) {
		case panelType.dmExplorer: {
			const parentPanelData = yield select((state: WebInterfaceBaseState) =>
				state.modules.dm.panels.get(payload.parentPanel.panelGuid)
			);
			if (parentPanelData?.dmViewerIsActive && parentPanelData?.dmViewerPanelId != '') {
				closePanelId = parentPanelData.dmViewerPanelId;
			}
			break;
		}
		case panelType.dmDashboard: {
			const closePanelGuid = yield select(
				(state: WebInterfaceBaseState) => state.modules.dm.dashboardChildPanelGuid
			);
			if (closePanelGuid != '') {
				closePanelId = closePanelGuid;
			}
			break;
		}
	}

	return closePanelId;
}

export function* getViewerPermissions(secretKey: string) {
	try {
		const contextResponse =
			yield ServiceList.dm.DocumentManagement.Explorer.GetObjectsContext.call({
				secretKeys: [secretKey],
				excludeParentTrees: true,
				excludeProperties: true
			});
		if (contextResponse) {
			const key = Object.keys(contextResponse.permissions)[0];
			return DmDataPermissionsFactoryFromJS(contextResponse.permissions[key]).toJS();
		}
		return DmDataPermissionsFactory().toJS();
	} catch (error) {
		return DmDataPermissionsFactory().toJS();
	}
}

function* getDmDataPermissions(dmData: IDmData) {
	try {
		if (dmData.contextLoaded.permissions) {
			return dmData.permissions;
		}
		const contextLoadedData: Map<string, DmData> = yield call(
			callGetObjectContext,
			Map({ [dmData.id]: DmDataFactoryFromJS(dmData) }),
			{ ExcludeParentTrees: true, ExcludeProperties: true }
		);
		return contextLoadedData.get(dmData.id).permissions.toJS();
	} catch (error) {
		return DmDataPermissionsFactory().toJS();
	}
}

function* setActiveChildPanel(parent: IRelatedPanel, panelGuid: string) {
	if (parent?.panelType == panelType.dmExplorer) {
		const state: WebInterfaceBaseState = yield select();
		let panel: DmPanel = state.modules.dm.panels.get(parent.panelGuid);

		panel = panel.set('dmViewerIsActive', true).set('dmViewerPanelId', panelGuid);

		yield put(
			updatePanel({
				panelId: parent.panelGuid,
				data: panel
			})
		);
	}
}
